/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';

import { styled } from '@uhg-abyss/web/tools/styled';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';

const InputFieldWrapper = styled('div', {
  marginVertical: '$core.spacing.100',
});

const InputErrorRow = styled('div', {
  marginTop: '$core.spacing.100',
  flexDirection: 'row',
  alignItems: 'center',
});

const InputErrorIcon = styled(IconSymbol, {
  marginRight: '$core.spacing.100',
});

const InputErrorText = styled(Text, {
  fontSize: '$core.font-size.p.60',
  fontWeight: '$core.font-weight.semibold',
  color: '$core.color.red.100',
});

interface BaseTextInputNodeProps {
  testID?: string;
  label?: string;
  hintText?: string;
  value?: string;
  onChangeText?: (value: string) => void;
  onBlur?: () => void;
  errorMessage?: string;
  placeholder?: string;
}

const InputFieldLabel = styled(Text, {
  marginBottom: '$core.spacing.100',
  fontSize: '$core.font-size.p.80',
  fontWeight: '$core.font-weight.bold',
  color: '$core.color.neutral.90',
});

const InputFieldHintText = styled(Text, {
  minHeight: '$core.sizing.100',
  marginBottom: '$core.spacing.100',
  fontSize: '$core.font-size.p.60',
  color: '$core.color.neutral.60',
});

const InputBox = styled('input', {
  flex: 1,
  paddingHorizontal: '$core.spacing.150',
  paddingVertical: '$core.spacing.150',
  backgroundColor: '$core.color.neutral.0',
  color: '$core.color.neutral.100',
  fontSize: '$core.font-size.p.80',
  fontWeight: '$core.font-weight.bold',
});

const InputContentRow = styled('div', {
  flexDirection: 'row',
  alignItems: 'center',
  minHeight: '$core.sizing.600',
  borderWidth: 1,
  borderColor: '$core.color.neutral.30',
  borderRadius: '$core.border-radius.md',
  backgroundColor: '$core.color.neutral.0',
  overflow: 'hidden',
  props: ({ isFocused, hasError }: { isFocused?: boolean; hasError?: boolean }) => ({
    borderColor: hasError
      ? '$core.color.red.100'
      : isFocused
        ? '$core.color.brand.80'
        : '$core.color.neutral.30',
  }),
});

const ClearButton = styled('button', {
  marginRight: '$core.spacing.150',
  alignItems: 'center',
  justifyContent: 'center',
});

const BaseTextInputNode: React.FC<BaseTextInputNodeProps> = ({
  testID,
  label,
  hintText,
  value,
  onChangeText,
  onBlur,
  errorMessage,
  placeholder,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<any>(null);
  const showClearButton = Boolean(value && value.length > 0);

  const handleClear = () => {
    onChangeText?.('');
    inputRef.current?.focus?.();
  };

  return (
    <InputFieldWrapper>
      {label ? <InputFieldLabel>{label}</InputFieldLabel> : null}
      {hintText !== undefined ? <InputFieldHintText>{hintText}</InputFieldHintText> : null}
      <InputContentRow isFocused={isFocused} hasError={Boolean(errorMessage)}>
        <InputBox
          ref={inputRef}
          testID={testID}
          value={value}
          placeholder={placeholder}
          underlineColorAndroid="transparent"
          onChangeText={onChangeText}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
        />
        {showClearButton ? (
          <ClearButton
            accessibilityRole="button"
            accessibilityLabel="Clear text"
            onPress={handleClear}
          >
            <IconSymbol
              icon="cancel"
              variant="outlined"
              size="$input-field.sizing.icon.all.utility"
              color="$core.color.neutral.60"
            />
          </ClearButton>
        ) : null}
      </InputContentRow>
      {errorMessage ? (
        <InputErrorRow>
          <InputErrorIcon
            icon="error"
            variant="outlined"
            size="$input-message.sizing.all.icon.leading"
            color="$core.color.red.100"
          />
          <InputErrorText>{errorMessage}</InputErrorText>
        </InputErrorRow>
      ) : null}
    </InputFieldWrapper>
  );
};

export { BaseTextInputNode };
